/**
 * P3-S3 FS-14-02 공동 작품 영상(교사 · SR-REC-04 · C-10 렌더 워커 — 계약 §3 교사 API · 세션 역할 teacher · 코스 경계):
 *   GET  /api/courses/{c}/teacher/videos?roomId= → { items:[{ jobId, replayId, roomId, source: relay|carry|room, title,
 *        status: queued|rendering|retrying|failed|done|dead, queuePos, progress, attempts, maxAttempts, durationS, sizeBytes,
 *        resolution 720|1080, cut full|stages, createdAt, finishedAt, feCode, expiresAt }] }(최신 먼저 · 빈 값 null) — 룸이 닫혀도 읽힌다
 *        · feCode FE-504 = 리플레이 만료 · replayId 거르기는 셸이 한다(서버가 받지 않아도)
 *   POST /api/courses/{c}/teacher/videos { replayId, resolution?, cut? } → 작업 하나(같은 활성 요청 · done 이 있으면 그것)
 *        · 만료 리플레이 410/422 FE-504
 *   POST …/videos/{jobId}/retry → 작업(dead → queued · attempts 0) · 만료 FE-504
 *   GET  …/videos/{jobId}/download → { url(/files/render/{job}.mp4?exp=&sig= · 5분), fileName, expiresAt, sizeBytes } · 409 · 410 FE-504
 *   POST …/videos/{jobId}/notice → 201/200 { draftId, editUrl, videoStatus, attachedAt }(Campus 반 공지 초안 · 멱등) · 202 { rerender: 작업 }(720p)
 *        · 413 {reason: video-too-large} · 502 {reason: campus-…}(연동 설정) · 503 · 409 · 410 FE-504 (2026-09-28 · SR-REC-04 · CRQ-003 D-1)
 * 이름(별명)은 영상 안 라벨에만 — 목록에는 제목·출처만. 점수·순위 필드 없음.
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { readConfig } from "../../../lib/config";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type {
  VideoStatus,
  VideoJob,
  VideoDownload,
  VideoNoticeResult,
  VideoRequest,
} from "../types";

/** 만료된 리플레이(FE-504) — 렌더 요청·다시 요청을 받지 않는다 */
export class VideoExpired extends Error {
  constructor() {
    super("replay expired");
    this.name = "VideoExpired";
  }
}

/** 공지로 보내지 못했다 — tooLarge(720p 로도 한도 밖) · setup(Campus 연동 설정 — 502) · failed(그 밖 · 잠시 뒤 다시) */
export class VideoNoticeFailed extends Error {
  constructor(readonly reason: "tooLarge" | "setup" | "failed") {
    super(`notice ${reason}`);
    this.name = "VideoNoticeFailed";
  }
}

export interface VideoApi {
  list(filter?: { roomId?: string | null; replayId?: string | null }): Promise<VideoJob[]>;
  request(body: VideoRequest): Promise<VideoJob | null>;
  retry(jobId: string): Promise<VideoJob | null>;
  download(jobId: string): Promise<VideoDownload>;
  notice(jobId: string): Promise<VideoNoticeResult>;
}

/** 재시도 한도(제어 평면 3회 뒤 dead · FE-502) */
export const VIDEO_MAX_ATTEMPTS = 3;

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const num = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

const STATUSES: readonly string[] = ["queued", "rendering", "retrying", "done", "dead"];

function readStatus(v: unknown): VideoStatus {
  const s = str(v);
  if (s === "failed") return "retrying";
  if (s === "running") return "rendering";
  return s && STATUSES.includes(s) ? (s as VideoStatus) : "queued";
}

/** 제목 — 문자열 또는 { course, activity }(렌더 claim 과 같은 모양) */
function readTitle(v: unknown): string {
  const s = str(v);
  if (s) return s;
  const o = obj(v);
  if (!o) return "";
  return [str(o.activity), str(o.course)].filter(Boolean).join(" · ");
}

/** 작업 한 줄을 너그럽게 읽는다 — 모양이 어긋나면 null(목록에서 뺀다) */
export function readJob(v: unknown): VideoJob | null {
  const o = obj(v);
  const jobId = str(o?.jobId) ?? (typeof o?.jobId === "number" ? String(o.jobId) : null);
  if (!o || !jobId) return null;
  const pct = num(o.progress);
  const pos = num(o.queuePos);
  return {
    jobId,
    replayId: str(o.replayId) ?? "",
    source: o.source === "carry" ? "carry" : o.source === "room" ? "room" : "relay",
    roomId: str(o.roomId),
    title: readTitle(o.title),
    status: readStatus(o.status),
    queuePos: pos !== null && pos > 0 ? Math.floor(pos) : null,
    progress: pct !== null ? Math.max(0, Math.min(100, Math.round(pct))) : null,
    attempts: Math.max(0, Math.floor(num(o.attempts) ?? 0)),
    maxAttempts: Math.max(1, Math.floor(num(o.maxAttempts) ?? VIDEO_MAX_ATTEMPTS)),
    durationS: num(o.durationS),
    sizeBytes: num(o.sizeBytes),
    resolution: num(o.resolution) === 720 ? 720 : 1080,
    cut: o.cut === "stages" ? "stages" : "full",
    createdAt: str(o.createdAt),
    finishedAt: str(o.finishedAt),
    feCode: str(o.feCode),
    expiresAt: str(o.expiresAt),
    notice: readNotice(o.notice),
  };
}

function readNotice(v: unknown): VideoJob["notice"] {
  const o = obj(v);
  const draftId = str(o?.draftId);
  return draftId ? { draftId, attachedAt: str(o?.attachedAt) } : null;
}

/** 공지 응답 — rerender 가 있으면 720p 작업 · 아니면 초안(editUrl 은 http(s) 만) */
export function readNoticeResult(v: unknown): VideoNoticeResult {
  const o = obj(v) ?? {};
  if (o.rerender !== undefined) return { kind: "rerender", job: readJob(o.rerender) };
  const draftId = str(o.draftId);
  const editUrl = str(o.editUrl);
  if (!draftId || !editUrl || !/^https?:\/\//.test(editUrl))
    throw new ApiError("NET-SERVER", 200, "notice outside contract", v);
  return {
    kind: "draft",
    draftId,
    editUrl,
    videoStatus: str(o.videoStatus),
    attachedAt: str(o.attachedAt),
  };
}

export function readJobs(v: unknown): VideoJob[] {
  const list = Array.isArray(v) ? v : (obj(v)?.videos ?? obj(v)?.items);
  return (Array.isArray(list) ? list : []).map(readJob).filter((j): j is VideoJob => !!j);
}

/**
 * 다운로드 응답 — url 은 제어 평면 원점의 절대 경로(/files/render/{job}.mp4?exp=&sig= · 5분)라 apiBase 를 붙인다.
 * http(s):/data:/blob: 는 그대로.
 */
export function readDownload(v: unknown, apiBase = ""): VideoDownload {
  const o = obj(v) ?? {};
  const raw = str(o.url) ?? str(o.downloadUrl);
  if (!raw) throw new ApiError("NET-SERVER", 200, "download url missing", v);
  const url =
    raw.startsWith("/") && !raw.startsWith("//") ? `${apiBase.replace(/\/+$/, "")}${raw}` : raw;
  return {
    url,
    fileName: str(o.fileName),
    expiresAt: str(o.expiresAt),
    sizeBytes: num(o.sizeBytes),
  };
}

const expired = (e: unknown): never => {
  if (e instanceof ApiError && (e.code === "FE-504" || e.status === 410)) throw new VideoExpired();
  throw e;
};

export function createVideoApi(request: Requester, courseId: string, apiBase = ""): VideoApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/videos`;
  const post = (body?: unknown): RequestInit => ({
    method: "POST",
    ...(body !== undefined
      ? { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }
      : {}),
  });
  return {
    async list(filter) {
      const q = new URLSearchParams();
      if (filter?.roomId) q.set("roomId", filter.roomId);
      if (filter?.replayId) q.set("replayId", filter.replayId);
      const qs = q.toString();
      const jobs = readJobs(await request<unknown>(qs ? `${c}?${qs}` : c));
      // 서버가 replayId 거르기를 모르더라도 화면은 그 리플레이 것만
      return filter?.replayId ? jobs.filter((j) => j.replayId === filter.replayId) : jobs;
    },
    async request(body) {
      try {
        return readJob(await request<unknown>(c, post(body)));
      } catch (e) {
        return expired(e);
      }
    },
    async retry(jobId) {
      try {
        return readJob(await request<unknown>(`${c}/${encodeURIComponent(jobId)}/retry`, post()));
      } catch (e) {
        return expired(e);
      }
    },
    async notice(jobId) {
      let v: unknown;
      try {
        v = await request<unknown>(`${c}/${encodeURIComponent(jobId)}/notice`, post());
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.code === "FE-504" || e.status === 410) throw new VideoExpired();
          if (e.status === 413) throw new VideoNoticeFailed("tooLarge");
          if (e.status === 502) throw new VideoNoticeFailed("setup");
          if (e.status === 409 || e.status === 503 || e.code === "NET-SERVER")
            throw new VideoNoticeFailed("failed");
        }
        throw e;
      }
      return readNoticeResult(v);
    },
    async download(jobId) {
      try {
        return readDownload(
          await request<unknown>(`${c}/${encodeURIComponent(jobId)}/download`),
          apiBase,
        );
      } catch (e) {
        return expired(e);
      }
    },
  };
}

export function useVideoApi(): VideoApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createVideoApi(manager.request, courseId, readConfig().apiBase) : null),
    [manager, courseId],
  );
}
